import {type ReactNode} from 'react';
import {formatNumber} from './format';
import {PERCENT_BANDS, formatMonth, heat, heatBackground, pivot} from './matrix';
import {type ReportMatrixBlock} from '@/api/reporting';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {TablePanel} from '@/components/ui/table-panel';
import {cn} from '@/lib/utils';

// The sheet's own word for a cell with nothing behind it (a rate over no submissions).
const NOT_APPLICABLE = 'N/A';

function Legend({format}: {readonly format: ReportMatrixBlock['format']}) {
  if (format === 'percent') {
    return (
      <div className="text-muted-foreground flex shrink-0 items-center gap-2 text-xs">
        {PERCENT_BANDS.map((band, index) => (
          <span key={band} className="flex items-center gap-1">
            <span
              aria-hidden
              className="size-3 rounded-sm border"
              style={{background: heatBackground((index + 1) / PERCENT_BANDS.length)}}
            />
            {band}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="text-muted-foreground flex shrink-0 items-center gap-2 text-xs">
      Low
      <span
        aria-hidden
        className="h-3 w-16 rounded-sm border"
        style={{background: `linear-gradient(to right, transparent, ${heatBackground(1)})`}}
      />
      High
    </div>
  );
}

// A month-by-row grid, as the analysts' sheet lays it out. Shading is a reading aid only: the number
// is always in the cell, so colour never carries it alone.
export function MatrixBlock({
  block,
  actions
}: {
  readonly block: ReportMatrixBlock;
  readonly actions?: ReactNode;
}) {
  const grid = pivot(block);
  const shaded = grid.rows.filter((row) => row !== block.total_row);
  const max = Math.max(
    0,
    ...shaded.flatMap((row) => grid.columns.map((column) => grid.value(row, column) ?? 0))
  );
  const columnLabel = (value: string) =>
    block.column.format === 'month' ? formatMonth(value) : value;

  return (
    <section data-block-id={block.id} data-block-type={block.type}>
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <h3 className="font-heading text-sm font-medium">{block.title}</h3>
          {block.description ? (
            <p className="text-muted-foreground text-xs">{block.description}</p>
          ) : null}
        </div>
        {actions}
        {block.heatmap ? <Legend format={block.format} /> : null}
      </div>
      <TablePanel>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{block.row.label}</TableHead>
              {grid.columns.map((column) => (
                <TableHead key={column} className="text-right">
                  {columnLabel(column)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {grid.rows.length === 0 ? (
              <TableRow>
                <TableCell className="text-muted-foreground py-6 text-center">
                  Nothing matched this range.
                </TableCell>
              </TableRow>
            ) : null}
            {grid.rows.map((row) => {
              const isTotal = row === block.total_row;
              return (
                <TableRow key={row} className={cn(isTotal && 'bg-muted/50 font-medium')}>
                  <TableCell className="font-medium">{row}</TableCell>
                  {grid.columns.map((column) => {
                    const value = grid.value(row, column);
                    const level = block.heatmap && !isTotal ? heat(value, block.format, max) : null;
                    return (
                      <TableCell
                        key={column}
                        data-heat={level ?? undefined}
                        className="relative text-right tabular-nums">
                        {/* A fill behind the number, since the design system's cell takes no style. */}
                        {level === null ? null : (
                          <span
                            aria-hidden
                            className="absolute inset-0"
                            style={{background: heatBackground(level)}}
                          />
                        )}
                        <span className="relative">
                          {value === null ? NOT_APPLICABLE : formatNumber(value, block.format)}
                        </span>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TablePanel>
    </section>
  );
}
